import { Form } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { Store } from 'antd/lib/form/interface'
import { useEffect, useState } from 'react'
import FormlessModal from './FormlessModal'
import { CIMSModalProps } from './types'

/**
 * A Wrapper around Ant Design's Modal component meant to standardize modals across
 * the application and expose only necessary props.
 *
 * Use this in conjunction with
 * - {@link CIMSModalWrapperProps}
 * - {@link useCIMSModal}
 *
 * Not all modal based components have moved to this modal, but all components
 * using Ant Design modals should be able to easily switch over
 *
 * @see {@link FormlessModal}
 */
export function CIMSModal<T = any>({
  initialFormValues,
  onCancel,
  onOk,
  children,
  loading,
  disabled,
  externalForm,
  ...rest
}: CIMSModalProps<T>) {
  const [_loading, setLoading] = useState(loading)
  const ok = async () => {
    if (onOk === undefined) return
    try {
      const results = await form.validateFields()
      await onOk(results)
    } catch (err) {
      // ignore errors, the component will display them
      return
    }
  }

  useEffect(() => {
    setLoading(loading)
    return () => setLoading(false)
  }, [loading])

  const [internalForm] = useForm()
  const form = externalForm ?? internalForm

  useEffect(() => {
    /**
     * This is the spot that causes issues with updating state variables
     * and form values
     *
     * Removing form as a dependency fixes it, but this seems to be the only
     * way to properly reset the modal on close
     *
     * Resetting fields in here when using an external form causes more issues
     * than it fixes. External forms will need to be managed more carefully
     * where they are instantiated
     */
    if (!externalForm && initialFormValues)
      form.setFieldsValue(initialFormValues)
  }, [initialFormValues, form, externalForm])
  return (
    <FormlessModal
      {...rest}
      /**
       * form.resetFields only affects the form if you set initial values!
       */
      afterClose={() => form.resetFields()}
      ok={{ fn: ok, loading: _loading, props: { disabled } }}
      cancel={{ fn: onCancel }}
    >
      <Form
        /**
         * we kind of have to cast this as {@link Store} here. Otherwise all the onOk
         * props will fail. we've got so comfortable being able to set the type in
         * those callbacks that forcing a constraint would be more work than it's worth
         */
        initialValues={initialFormValues as Store}
        form={form}
      >
        {children}
      </Form>
    </FormlessModal>
  )
}
